import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Settings2, MoreVertical, Wifi, Link, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { flowBoxDeviceStatus } from "../../../Config/constants";
import Button from "../../Shared/Buttons/Button";
import IconButton from "../../Shared/Buttons/IconButton";
import Chip from "../../Shared/ui/Chip";
import { color, radius } from "../../Shared/tokens";
import { FlowBoxIllustration } from "./FlowBoxPairingGuide";
import FlowBoxPairingGuideDialog from "./FlowBoxPairingGuideDialog";
import {
  Dialog as SharedDialog,
  DialogHeader,
  DialogTitle as SharedDialogTitle,
  DialogBody,
  DialogFooter,
} from "../../Shared/Dialog";

const formatDate = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  return isNaN(d) ? "-" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

// ──────────────────────────────────────────────────────────────────────────────
// ConfirmDialog
// ──────────────────────────────────────────────────────────────────────────────
function ConfirmDialog({ open, title, message, confirmLabel, onConfirm, onCancel, destructive = false }) {
  const { t } = useTranslation();
  return (
    <SharedDialog open={open} onClose={onCancel} maxWidth="xs">
      <DialogHeader closeable={false}>
        <SharedDialogTitle>{title}</SharedDialogTitle>
      </DialogHeader>
      <DialogBody>
        <p style={{ margin: 0, fontSize: 14, fontFamily: "Lato, sans-serif", color: "#6b7280" }}>{message}</p>
      </DialogBody>
      <DialogFooter>
        <Button variant="light" size="small" onClick={onCancel}>{t("common.cancel")}</Button>
        <Button
          variant={destructive ? "destructive" : "primary"}
          size="small"
          onClick={onConfirm}
        >
          {confirmLabel || t("common.confirm")}
        </Button>
      </DialogFooter>
    </SharedDialog>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// KebabMenu
// ──────────────────────────────────────────────────────────────────────────────
function KebabMenu({ items }) {
  const { t } = useTranslation();
  const classes = useKebabStyles();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className={classes.root} ref={ref}>
      <IconButton size="small" variant="ghost" ariaLabel={t("flowBox.configure.deviceActions")} onClick={() => setOpen((v) => !v)}>
        <MoreVertical />
      </IconButton>
      {open && (
        <div className={classes.menu}>
          {items.map((item) => (
            <button
              key={item.label}
              className={`${classes.item} ${item.destructive ? classes.destructive : ""}`}
              onClick={() => { setOpen(false); item.onClick(); }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// PendingConfigurationCard — PendingConfiguration status (cloned, awaiting IMEI)
// ──────────────────────────────────────────────────────────────────────────────
export function PendingConfigurationCard({ device, onAssignImei, onConfigure }) {
  const { t } = useTranslation();
  const classes = useCardStyles();

  return (
    <div className={`${classes.card} ${classes.pendingConfigCard}`}>
      <div className={classes.cardHeader}>
        <div className={classes.cardTitleRow}>
          <span className={classes.cardName}>
            {device.displayName || <span className={classes.unnamed}>{t("flowBox.card.unnamed")}</span>}
          </span>
          <Chip variant="warning" size="small">
            {t("flowBox.status.pendingConfiguration")}
          </Chip>
        </div>
        <span className={`${classes.cardImei} ${classes.noImei}`}>{t("flowBox.card.noImei")}</span>
      </div>

      <div className={classes.pendingConfigBody}>
        <Link size={16} className={classes.pendingConfigIcon} />
        <span className={classes.pendingConfigLabel}>{t("flowBox.card.pendingConfigurationHint")}</span>
      </div>

      <div className={classes.cardActions}>
        <Button
          size="small"
          variant="secondary"
          startIcon={<Settings2 size={14} />}
          onClick={() => onConfigure(device)}
          style={{ flex: 1 }}
        >
          {t("flowBox.card.configure")}
        </Button>
        <Button
          size="small"
          startIcon={<Link size={14} />}
          onClick={() => onAssignImei(device)}
          style={{ flex: 1 }}
        >
          {t("flowBox.card.assignDevice")}
        </Button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// PendingDeviceCard — Registered status (searching for device)
// ──────────────────────────────────────────────────────────────────────────────
export function PendingDeviceCard({ device, onCancelSearch }) {
  const { t } = useTranslation();
  const classes = useCardStyles();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  return (
    <div className={`${classes.card} ${classes.pendingCard}`}>
      <div className={classes.cardHeader}>
        <div className={classes.cardTitleRow}>
          <span className={classes.cardName}>
            {device.displayName || <span className={classes.unnamed}>{t("flowBox.card.unnamed")}</span>}
          </span>
          <KebabMenu
            items={[{ label: t("flowBox.card.stopSearching"), destructive: true, onClick: () => setConfirmCancel(true) }]}
          />
        </div>
        <span className={classes.cardImei}>{device.imei}</span>
      </div>

      <div className={classes.pendingBody}>
        <div className={classes.pulsingDot} />
        <span className={classes.pendingLabel}>{t("flowBox.card.searchingForDevice")}</span>
      </div>

      <div className={classes.pairingHint}>
        <FlowBoxIllustration buttonCount={device.buttonCount || 5} width={140} />
        <div className={classes.pairingHintInfo}>
          <IconButton size="small" variant="ghost" ariaLabel={t("flowBox.pairing.howToConnect")} onClick={() => setGuideOpen(true)}>
            <Info />
          </IconButton>
        </div>
      </div>

      <FlowBoxPairingGuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        buttonCount={device.buttonCount || 5}
      />

      <ConfirmDialog
        open={confirmCancel}
        title={t("flowBox.card.stopSearchingTitle")}
        message={t("flowBox.card.stopSearchingMessage", { name: device.displayName || device.imei })}
        confirmLabel={t("flowBox.card.stopSearching")}
        destructive
        onConfirm={() => { setConfirmCancel(false); onCancelSearch(device); }}
        onCancel={() => setConfirmCancel(false)}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// FoundDeviceCard — AvailableToConnect status (device found, awaiting user confirm)
// ──────────────────────────────────────────────────────────────────────────────
export function FoundDeviceCard({ device, onConnect, onCancelSearch }) {
  const { t } = useTranslation();
  const classes = useCardStyles();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const handleConnect = () => {
    setConnecting(true);
    onConnect(device).finally(() => setConnecting(false));
  };

  return (
    <div className={`${classes.card} ${classes.foundCard}`}>
      <div className={classes.cardHeader}>
        <div className={classes.cardTitleRow}>
          <span className={classes.cardName}>
            {device.displayName || <span className={classes.unnamed}>{t("flowBox.card.unnamed")}</span>}
          </span>
          <KebabMenu
            items={[{ label: t("common.cancel"), destructive: true, onClick: () => setConfirmCancel(true) }]}
          />
        </div>
        <span className={classes.cardImei}>{device.imei}</span>
      </div>

      <div className={classes.foundBody}>
        <Wifi size={20} className={classes.foundIcon} />
        <div>
          <div className={classes.foundTitle}>{t("flowBox.card.deviceFoundTitle")}</div>
          <div className={classes.foundSub}>{t("flowBox.card.deviceFoundSub")}</div>
        </div>
      </div>

      <div className={classes.cardActions}>
        <Button
          size="small"
          variant="success"
          style={{ flex: 1 }}
          onClick={handleConnect}
          disabled={connecting}
          loading={connecting}
        >
          {t("flowBox.card.connect")}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmCancel}
        title={t("flowBox.card.cancelConnectionTitle")}
        message={t("flowBox.card.cancelConnectionMessage", { name: device.displayName || device.imei })}
        confirmLabel={t("common.cancel")}
        destructive
        onConfirm={() => { setConfirmCancel(false); onCancelSearch(device); }}
        onCancel={() => setConfirmCancel(false)}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DeviceCard — Active / Inactive status (fully connected device)
// ──────────────────────────────────────────────────────────────────────────────
const DEVICE_CHIP = {
  [flowBoxDeviceStatus.ACTIVE]:   { variant: "success", tKey: "flowBox.status.active" },
  [flowBoxDeviceStatus.INACTIVE]: { variant: "default", tKey: "flowBox.status.inactive" },
  [flowBoxDeviceStatus.SETUP]:    { variant: "info",    tKey: "flowBox.status.setup" },
};

export function DeviceCard({ device, onConfigure }) {
  const { t } = useTranslation();
  const classes = useCardStyles();
  const chip = DEVICE_CHIP[device.statusId] || DEVICE_CHIP[flowBoxDeviceStatus.INACTIVE];

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <div className={classes.cardTitleRow}>
          <span className={classes.cardName}>
            {device.displayName || <span className={classes.unnamed}>{t("flowBox.card.unnamed")}</span>}
          </span>
          <Chip variant={chip.variant} size="small">
            {t(chip.tKey)}
          </Chip>
        </div>
        <span className={classes.cardImei}>{device.imei}</span>
      </div>

      <div className={classes.cardMeta}>
        <div className={classes.metaRow}>
          <span className={classes.metaLabel}>{t("flowBox.card.lastSeen")}</span>
          <span className={classes.metaValue}>{formatDate(device.lastSeenOn)}</span>
        </div>
      </div>

      <div className={classes.cardActions}>
        <Button
          size="small"
          variant="secondary"
          startIcon={<Settings2 size={14} />}
          onClick={() => onConfigure(device)}
          style={{ flex: 1 }}
        >
          {t("flowBox.card.configure")}
        </Button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// SkeletonCard
// ──────────────────────────────────────────────────────────────────────────────
export function SkeletonCard() {
  const classes = useCardStyles();
  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <div className={classes.skeleton} style={{ width: "60%", height: 18, marginBottom: 8 }} />
        <div className={classes.skeleton} style={{ width: "45%", height: 13 }} />
      </div>
      <div className={classes.cardMeta}>
        {[0, 1].map((i) => (
          <div key={i} className={classes.skeleton} style={{ width: "100%", height: 13 }} />
        ))}
      </div>
      <div className={classes.cardActions}>
        <div className={classes.skeleton} style={{ flex: 1, height: 32, borderRadius: 4 }} />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// AnyDeviceCard — routes to the correct card based on device status
// ──────────────────────────────────────────────────────────────────────────────
export default function AnyDeviceCard({ device, onConfigure, onDeregister, onDeactivate, onConnect, onCancelSearch, onAssignImei }) {
  if (device.statusId === flowBoxDeviceStatus.PENDING_CONFIGURATION) {
    return <PendingConfigurationCard device={device} onAssignImei={onAssignImei} onConfigure={onConfigure} />;
  }
  if (device.statusId === flowBoxDeviceStatus.REGISTERED) {
    return <PendingDeviceCard device={device} onCancelSearch={onCancelSearch} />;
  }
  if (device.statusId === flowBoxDeviceStatus.AVAILABLE_TO_CONNECT) {
    return <FoundDeviceCard device={device} onConnect={onConnect} onCancelSearch={onCancelSearch} />;
  }
  return (
    <DeviceCard
      device={device}
      onConfigure={onConfigure}
      onDeregister={onDeregister}
      onDeactivate={onDeactivate}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────────────────────────────────
const useCardStyles = makeStyles(() => ({
  card: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  pendingConfigCard: {
    border: "1px dashed #d1d5db",
    backgroundColor: "#fafafa",
  },
  pendingConfigBody: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "10px 16px 14px",
  },
  pendingConfigIcon: {
    color: "#9ca3af",
    flexShrink: 0,
    marginTop: 1,
  },
  pendingConfigLabel: {
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "#6b7280",
  },
  noImei: {
    fontStyle: "italic",
    color: "#9ca3af",
  },
  pendingCard: {},
  foundCard: {},
  cardHeader: {
    padding: "16px 16px 10px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  cardTitleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "8px",
  },
  cardName: {
    fontSize: "18px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: color.text.primary,
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  unnamed: {
    color: "#9ca3af",
    fontStyle: "italic",
    fontWeight: "normal",
  },
  cardImei: {
    fontFamily: "'Courier New', Courier, monospace",
    fontSize: "12px",
    color: color.text.secondary,
    letterSpacing: "0.04em",
  },
  pendingBody: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 16px 12px",
  },
  pulsingDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "#f59e0b",
    flexShrink: 0,
    animation: "$dotPulse 1.6s ease-in-out infinite",
  },
  pendingLabel: {
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "#6b7280",
  },
  pairingHint: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    margin: "0 16px 16px",
    padding: "14px 12px",
    borderRadius: radius.md,
    backgroundColor: color.surface.subtle,
    border: `1px solid ${color.border.default}`,
  },
  pairingHintInfo: {
    position: "absolute",
    top: 4,
    right: 4,
  },
  foundBody: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "10px 16px 14px",
  },
  foundIcon: {
    color: "var(--color-success)",
    flexShrink: 0,
    marginTop: 1,
  },
  foundTitle: {
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "700",
    color: "var(--color-success)",
    marginBottom: 2,
  },
  foundSub: {
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "#6b7280",
  },
  cardMeta: {
    padding: "2px 16px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "7px",
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  metaLabel: {
    color: "#6b7280",
    minWidth: "95px",
    flexShrink: 0,
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
  },
  metaValue: {
    color: "#1f2937",
    fontWeight: "500",
    fontFamily: "Lato, sans-serif",
    fontSize: "13px",
  },
  cardDivider: {
    height: "1px",
    backgroundColor: "#EDEDED",
    margin: "0 16px",
  },
  cardActions: {
    padding: "12px 16px",
    paddingBottom: "16px",
    display: "flex",
    gap: "6px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  skeleton: {
    backgroundColor: "#EDEDED",
    borderRadius: "4px",
    animation: "$pulse 1.6s ease-in-out infinite",
  },
  "@keyframes pulse": {
    "0%":   { opacity: 1 },
    "50%":  { opacity: 0.4 },
    "100%": { opacity: 1 },
  },
  "@keyframes dotPulse": {
    "0%":   { opacity: 1, transform: "scale(1)" },
    "50%":  { opacity: 0.4, transform: "scale(0.75)" },
    "100%": { opacity: 1, transform: "scale(1)" },
  },
}));

const useKebabStyles = makeStyles(() => ({
  root: {
    position: "relative",
    flexShrink: 0,
  },
  menu: {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
    borderRadius: 6,
    boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
    zIndex: 100,
    minWidth: 150,
    padding: "4px 0",
  },
  item: {
    display: "block",
    width: "100%",
    padding: "8px 14px",
    fontSize: 13,
    fontFamily: "Lato, sans-serif",
    textAlign: "left",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#1f2937",
    "&:hover": {
      backgroundColor: "#F8F8F8",
    },
  },
  destructive: {
    color: "#D84949",
    "&:hover": {
      backgroundColor: "#fff5f5",
    },
  },
}));
